import { ConstructionSiteFullDetails, GFAFullDetails } from "@dune/types";
import { formatDateForInput } from "@dune/utils";
import { ACTION_BUILDING_TYPES, defaultClientSocietyValues } from "@dune/vars";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Resolver, useForm } from "react-hook-form";
import { z } from "zod";
import { requiredNumericString } from "../../../utils/zodUtils";
import { societyClientSchema } from "./useCreateConstructionSiteForm";

// Type definition to replace SafeParseReturnType
type ParseResult<T> = {
	success: boolean;
	error?: z.ZodError;
	data?: T;
};

export type ActionsBuildingDetailsError = {
	buildingDetails: {
		[key in keyof typeof ACTION_BUILDING_TYPES]: {
			[key: string]: { message: string };
		};
	};
};

export const buildingDetailsDefautValues = {
	PRIVATE_WORK: {
		fromFloor: "",
		toFloor: "",
		surface: "",
	},
	APPARTMENT: {
		fromFloor: "",
		toFloor: "",
		surface: "",
		extensionSurface: "",
	},
	INDIVIDUAL_HOUSE: {
		floorsCount: "",
		basementsCount: "",
		surface: "",
		habitationsCount: "",
		extensionSurface: "",
	},

	COMMON_PARTS: {
		buildingsCount: "",
		maxFloors: "",
		maxBasements: "",
		surface: "",
	},
	LIVING_BUILDING: {
		buildingsCount: "",
		maxFloors: "",
		maxBasements: "",
		surface: "",
		habitationsCount: "",
		extensionSurface: "",
	},
	LOT_HOUSES: {
		buildingsCount: "",
		maxFloors: "",
		maxBasements: "",
		surface: "",
		habitationsCount: "",
		extensionSurface: "",
	},
	OFFICE_BUILDING: {
		buildingsCount: "",
		maxFloors: "",
		maxBasements: "",
		surface: "",
		extensionSurface: "",
	},
	COMMERCIAL_BUILDING: {
		buildingsCount: "",
		maxFloors: "",
		maxBasements: "",
		surface: "",
		extensionSurface: "",
	},
	MIXT_BUILDING: {
		buildingsCount: "",
		maxFloors: "",
		maxBasements: "",
		surface: "",
		habitationsCount: "",
		extensionSurface: "",
		habitationsSize: "",
		officesSize: "",
		commercialBuildingsSize: "",
	},
};

const multipleBuildingDetailsSchema = z.object({
	buildingsCount: z.string(),
	maxFloors: z.string(),
	maxBasements: z.string(),
	surface: z.string(),
	habitationsCount: z.string(),
	extensionSurface: z.string(),
});

const privateWorkSchema = z.object({
	fromFloor: z.string(),
	toFloor: z.string(),
	surface: z.string(),
});

const buildingDetailsSchema = z.object({
	COMMON_PARTS: multipleBuildingDetailsSchema.omit({
		habitationsCount: true,
		extensionSurface: true,
	}),
	LIVING_BUILDING: multipleBuildingDetailsSchema,
	LOT_HOUSES: multipleBuildingDetailsSchema,
	OFFICE_BUILDING: multipleBuildingDetailsSchema.omit({
		habitationsCount: true,
	}),
	COMMERCIAL_BUILDING: multipleBuildingDetailsSchema.omit({
		habitationsCount: true,
	}),
	MIXT_BUILDING: multipleBuildingDetailsSchema.extend({
		habitationsSize: z.string(),
		officesSize: z.string(),
		commercialBuildingsSize: z.string(),
	}),
	PRIVATE_WORK: privateWorkSchema,
	APPARTMENT: privateWorkSchema.extend({
		extensionSurface: z.string(),
	}),
	INDIVIDUAL_HOUSE: z.object({
		floorsCount: z.string(),
		basementsCount: z.string(),
		surface: z.string(),
		habitationsCount: z.string(),
		extensionSurface: z.string(),
	}),
});

export const defaultActionValues = {
	types: [],
	hasComplementaryActions: false,
	complementaryActions: {
		types: [],
		buildingType: undefined,
	},
	buildingDetails: buildingDetailsDefautValues,
	buildingUsage: undefined,
	underpinningExistingFoundations: false,
	interventionOnLoadBearingStructure: false,
	socialLandlordRental: false,
};

const baseBuildingSchema = z.object({
	buildingsCount: requiredNumericString(
		"Le nombre de bâtiments est obligatoire",
		"Le nombre de bâtiments doit être un nombre valide",
	),
	maxFloors: requiredNumericString(
		"Le nombre d'étages est obligatoire",
		"Le nombre d'étages doit être un nombre valide",
	),
	maxBasements: requiredNumericString(
		"Le nombre de sous-sols est obligatoire",
		"Le nombre de sous-sols doit être un nombre valide",
	),
	surface: requiredNumericString(
		"La surface est obligatoire",
		"La surface doit être un nombre valide",
	),
	extensionSurface: z
		.string()
		.optional()
		.transform((val) => (val ? Number(val) : undefined)),
});

const appartmentSchema = z.object({
	fromFloor: requiredNumericString(
		"Le numéro de l'étage est obligatoire",
		"Le numéro de l'étage doit être un nombre valide",
	),
	toFloor: requiredNumericString(
		"Le numéro de l'étage est obligatoire",
		"Le numéro de l'étage doit être un nombre valide",
	),
	surface: requiredNumericString(
		"La surface est obligatoire",
		"La surface doit être un nombre valide",
	),
	extensionSurface: z
		.string()
		.optional()
		.transform((val) => (val ? Number(val) : undefined)),
});

const individualHouseSchema = z.object({
	floorsCount: requiredNumericString(
		"Le nombre d'étages est obligatoire",
		"Le nombre d'étages doit être un nombre valide",
	),
	basementsCount: requiredNumericString(
		"Le nombre de sous-sols est obligatoire",
		"Le nombre de sous-sols doit être un nombre valide",
	),
	surface: requiredNumericString(
		"La surface est obligatoire",
		"La surface doit être un nombre valide",
	),
	habitationsCount: requiredNumericString(
		"Le nombre de logements est obligatoire",
		"Le nombre de logements doit être un nombre valide",
	),
	extensionSurface: z
		.string()
		.optional()
		.transform((val) => (val ? Number(val) : undefined)),
});

const buildingWithHabitationsSchema = baseBuildingSchema.extend({
	habitationsCount: requiredNumericString(
		"Le nombre de logements est obligatoire",
		"Le nombre de logements doit être un nombre valide",
	),
});

const mixtBuildingSchema = buildingWithHabitationsSchema.extend({
	habitationsSize: requiredNumericString(
		"La taille des logements est obligatoire",
		"La taille des logements doit être un nombre valide",
	),
	officesSize: requiredNumericString(
		"La taille des bureaux est obligatoire",
		"La taille des bureaux doit être un nombre valide",
	),
	commercialBuildingsSize: requiredNumericString(
		"La taille des bâtiments commerciaux est obligatoire",
		"La taille des bâtiments commerciaux doit être un nombre valide",
	),
});

const syndicSocietySchema = societyClientSchema;

const baseConstructionSiteDetailsSchema = z
	.object({
		projectName: z.string().optional(),
		operationType: z
			.enum(["VEFA", "VIR", "CPI", "GENERAL_CONTRACTOR", "OTHER"])
			.optional(),
		processesType: z
			.enum([
				"CONCRETE",
				"WOOD",
				"METAL",
				"MODULAR_WOOD",
				"MODULAR_METAL",
				"MODULAR_CONCRETE",
			])
			.optional(),
		rga: z.number().optional(),
		sismic: z.number().optional(),
		streetNumber: z.string().optional(),
		streetName: z.string().min(1, "Le nom de rue est obligatoire"),
		postalCode: z
			.string()
			.trim()
			.min(1, "Le code postal est obligatoire")
			.regex(
				/^(?!00)\d{5}$/,
				"Le code postal doit contenir exactement 5 chiffres",
			),
		city: z.string().min(1, "La ville est obligatoire"),
		latlon: z.string().optional(),
		georisquesAddress: z
			.object({
				streetNumber: z.string().optional(),
				streetName: z.string().optional(),
				postalCode: z.string().optional(),
				city: z.string().optional(),
			})
			.optional(),
		georisquesErrorMessage: z.string().optional(),
		actions: z.object({
			types: z
				.array(z.enum(["CONSTRUCTION", "RENOVATION", "EXTENSION", "SYNDIC"]))
				.optional(),
			buildingType: z
				.enum([
					"COMMON_PARTS",
					"PRIVATE_WORK",
					"APPARTMENT",
					"INDIVIDUAL_HOUSE",
					"LIVING_BUILDING",
					"COMMERCIAL_BUILDING",
					"LOT_HOUSES",
					"OFFICE_BUILDING",
					"MIXT_BUILDING",
				])
				.optional(),
			hasComplementaryActions: z.boolean(),
			complementaryActions: z
				.object({
					types: z.array(
						z.enum(["CONSTRUCTION", "RENOVATION", "EXTENSION", "SYNDIC"]),
					),
					buildingType: z
						.enum([
							"COMMON_PARTS",
							"PRIVATE_WORK",
							"APPARTMENT",
							"INDIVIDUAL_HOUSE",
							"LIVING_BUILDING",
							"COMMERCIAL_BUILDING",
							"LOT_HOUSES",
							"OFFICE_BUILDING",
							"MIXT_BUILDING",
						])
						.optional(),
				})
				.optional(),
			buildingDetails: buildingDetailsSchema,
			buildingUsage: z
				.enum(["LIVING", "COMMERCIAL_TERTIARY", "MIXT"])
				.optional(),
			underpinningExistingFoundations: z.boolean(),
			interventionOnLoadBearingStructure: z.boolean(),
			socialLandlordRental: z.boolean(),
		}),
		taxMode: z.enum(["HT", "TTC"]),
		totalAmountTTC: z.coerce.number().optional(),
		totalAmountHT: z.coerce.number().optional(),
		finalTotalAmount: z.number().optional(),
		startDate: z.string().min(1, "La date de début est obligatoire"),
		deliveryDate: z.string().min(1, "La date de réception est obligatoire"),
		cityHallStartDate: z.string().optional(),
		interventions: z.object({
			architect: z.object({
				ARCHITECT_CONCEPTION: z.boolean(),
				ARCHITECT_BUILDING_PERMIT_DEPOT: z.boolean(),
				ARCHITECT_WORK_FOLLOW_UP: z.boolean(),
			}),
			moe: z.object({
				MOE_CONCEPTION: z.boolean(),
				MOE_BUILDING_PERMIT_DEPOT: z.boolean(),
				MOE_WORK_FOLLOW_UP: z.boolean(),
			}),
			soil_study: z.object({
				G2AVP: z.boolean(),
				G2PRO: z.boolean(),
				G3: z.boolean(),
				G4: z.boolean(),
				G5: z.boolean(),
			}),
			technical_control: z.object({
				L: z.boolean(),
				LP: z.boolean(),
				LE: z.boolean(),
				AV: z.boolean(),
				PS: z.boolean(),
				S: z.boolean(),
				SH: z.boolean(),
				STI: z.boolean(),
				SEI: z.boolean(),
				HAND: z.boolean(),
				BRD: z.boolean(),
				TH: z.boolean(),
				PHH: z.boolean(),
				PHA: z.boolean(),
				PV: z.boolean(),
				F: z.boolean(),
			}),
			consulting: z.object({
				STRUCTURAL: z.boolean(),
				ACOUSTIC: z.boolean(),
				SANITATION: z.boolean(),
				INFILTRATION: z.boolean(),
				GEOTECHNICAL: z.boolean(),
				THERMAL: z.boolean(),
				FLUID: z.boolean(),
				VRD: z.boolean(),
				HQE: z.boolean(),
				LIGHTING: z.boolean(),
				FIRE_SAFETY: z.boolean(),
			}),
		}),
		ccmi: z.boolean(),
		nonUsualMethods: z.boolean().refine((val) => val === true, {
			message: "Veuillez cocher la case",
		}),
		noOtherOperations: z.boolean(),
	})
	.and(
		z.union([
			z.object({
				throughSyndic: z.literal(true),
				syndicSocietyId: z.number().optional(),
				syndicSociety: syndicSocietySchema,
			}),
			z.object({
				throughSyndic: z.literal(false),
			}),
		]),
	);

// Function to apply all construction site validations to any schema
export function applyConstructionSiteValidations<
	T extends typeof baseConstructionSiteDetailsSchema,
>(schema: T) {
	return schema
		.refine((data) => data.actions.types && data.actions.types.length > 0, {
			message: "Veuillez sélectionner au moins une opération",
			path: ["actions", "types"],
		})
		.refine(
			(data) => {
				if (
					data.actions.buildingType &&
					["OFFICE_BUILDING", "COMMERCIAL_BUILDING"].includes(
						data.actions.buildingType,
					)
				) {
					return data.taxMode === "HT" || data.taxMode === "TTC";
				}
				return true;
			},
			{
				message: "Veuillez sélectionner un mode de taxation",
				path: ["taxMode"],
			},
		)
		.refine(
			(data) => {
				if (data.taxMode === "HT") {
					return (
						data.totalAmountHT !== undefined &&
						data.totalAmountHT !== null &&
						data.totalAmountHT > 0
					);
				}
				return true;
			},
			{
				message: "Le montant doit être renseigné",
				path: ["totalAmountHT"],
			},
		)
		.refine(
			(data) => {
				if (data.taxMode === "TTC") {
					return (
						data.totalAmountTTC !== undefined &&
						data.totalAmountTTC !== null &&
						data.totalAmountTTC > 0
					);
				}
				return true;
			},

			{
				message: "Le montant doit être renseigné",
				path: ["totalAmountTTC"],
			},
		)
		.refine(
			(data) =>
				!data.startDate ||
				!data.deliveryDate ||
				data.startDate <= data.deliveryDate,
			{
				message: "La date de début doit être inférieure à la date de livraison",
				path: ["startDate"],
			},
		)
		.superRefine((data, ctx) => {
			if (
				data.actions.hasComplementaryActions &&
				data.actions.complementaryActions &&
				data.actions.complementaryActions.types?.length === 0
			) {
				ctx.addIssue({
					code: "custom",
					message:
						"Veuillez sélectionner au moins une opération complémentaire",
					path: ["actions", "complementaryActions", "types"],
				});
			}
		})
		.superRefine((data, ctx) => {
			if (
				data.actions.types &&
				data.actions.types.length > 0 &&
				!data.actions.buildingType
			) {
				ctx.addIssue({
					code: "custom",
					message: "Veuillez sélectionner un type de bâtiment",
					path: ["actions", "buildingType"],
				});
			}
		})
		.superRefine((data, ctx) => {
			if (
				data.actions.hasComplementaryActions &&
				data.actions.complementaryActions &&
				data.actions.complementaryActions.types.length > 0 &&
				!data.actions.complementaryActions.buildingType
			) {
				ctx.addIssue({
					code: "custom",
					message: "Veuillez sélectionner un type de bâtiment",
					path: ["actions", "complementaryActions", "buildingType"],
				});
			}
		})
		.superRefine((data, ctx) => {
			if (
				data?.actions?.types?.includes("CONSTRUCTION") &&
				data.actions.buildingType === "INDIVIDUAL_HOUSE" &&
				!data.ccmi
			) {
				ctx.addIssue({
					code: "custom",
					message: "Veuillez cocher la case",
					path: ["ccmi"],
				});
			}
		})
		.superRefine((data, ctx) => {
			const isSyndic = data.actions?.types?.includes("SYNDIC");
			const isCommonParts =
				data.actions?.buildingType === "COMMON_PARTS";
			const isPrivateWork =
				data.actions?.buildingType === "PRIVATE_WORK";
			const throughSyndic =
				"throughSyndic" in data && data.throughSyndic;

			if (
				(isSyndic && (isCommonParts || isPrivateWork)) ||
				throughSyndic
			) {
				if (!data.noOtherOperations) {
					ctx.addIssue({
						code: "custom",
						message: "Veuillez cocher la case",
						path: ["noOtherOperations"],
					});
				}
			}
		})
		.superRefine((data, ctx) => {
			if (!data.actions.buildingType) return;

			let building: ParseResult<unknown>;
			let schemaToParse: z.ZodTypeAny = z.null();
			const hasExtension = data.actions.types?.includes("EXTENSION");

			if (data.actions.buildingType === "COMMON_PARTS") {
				schemaToParse = baseBuildingSchema;
			} else if (data.actions.buildingType === "PRIVATE_WORK") {
				schemaToParse = appartmentSchema;
			} else if (data.actions.buildingType === "APPARTMENT") {
				schemaToParse = hasExtension
					? appartmentSchema.extend({
							extensionSurface: z
								.string()
								.min(1, "La surface d'extension est obligatoire")
								.transform((val) => Number(val))
								.refine(
									(val) => !Number.isNaN(val),
									"La surface d'extension doit être un nombre valide",
								),
						})
					: appartmentSchema;
			} else if (data.actions.buildingType === "INDIVIDUAL_HOUSE") {
				schemaToParse = hasExtension
					? individualHouseSchema.extend({
							extensionSurface: z
								.string()
								.min(1, "La surface d'extension est obligatoire")
								.transform((val) => Number(val))
								.refine(
									(val) => !Number.isNaN(val),
									"La surface d'extension doit être un nombre valide",
								),
						})
					: individualHouseSchema;
			} else if (
				["LIVING_BUILDING", "LOT_HOUSES"].includes(data.actions.buildingType)
			) {
				schemaToParse = hasExtension
					? buildingWithHabitationsSchema.extend({
							extensionSurface: z
								.string()
								.min(1, "La surface d'extension est obligatoire")
								.transform((val) => Number(val))
								.refine(
									(val) => !Number.isNaN(val),
									"La surface d'extension doit être un nombre valide",
								),
						})
					: buildingWithHabitationsSchema;
			} else if (
				["COMMERCIAL_BUILDING", "OFFICE_BUILDING"].includes(
					data.actions.buildingType,
				)
			) {
				schemaToParse = hasExtension
					? baseBuildingSchema.extend({
							extensionSurface: z
								.string()
								.min(1, "La surface d'extension est obligatoire")
								.transform((val) => Number(val))
								.refine(
									(val) => !Number.isNaN(val),
									"La surface d'extension doit être un nombre valide",
								),
						})
					: baseBuildingSchema;
			} else if (data.actions.buildingType === "MIXT_BUILDING") {
				schemaToParse = hasExtension
					? mixtBuildingSchema.extend({
							extensionSurface: z
								.string()
								.min(1, "La surface d'extension est obligatoire")
								.transform((val) => Number(val))
								.refine(
									(val) => !Number.isNaN(val),
									"La surface d'extension doit être un nombre valide",
								),
						})
					: mixtBuildingSchema;
			}

			if (!schemaToParse) return;

			building = schemaToParse.safeParse(
				data.actions.buildingDetails[data.actions.buildingType],
			);

			if (!building.success) {
				for (const error of building.error?.issues || []) {
					ctx.addIssue({
						...error,
						path: [
							"actions",
							"buildingDetails",
							data.actions.buildingType,
							...error.path,
						],
					});
				}
			}
		});
}

export const constructionSiteDetailsSchema = applyConstructionSiteValidations(
	baseConstructionSiteDetailsSchema,
);

// Note: nous n'utilisons plus ce type directement avec useForm, mais le conservons pour la compatibilité
export type ConstructionSiteDetailsFormData = z.infer<
	typeof constructionSiteDetailsSchema
>;

const constructionSiteDetailsDefaultValues: ConstructionSiteDetailsFormData = {
	projectName: "",
	operationType: undefined,
	processesType: undefined,
	streetNumber: "",
	streetName: "",
	postalCode: "",
	city: "",
	latlon: undefined,
	georisquesAddress: undefined,
	georisquesErrorMessage: undefined,
	throughSyndic: false,
	actions: defaultActionValues,
	taxMode: "TTC",
	totalAmountTTC: undefined,
	totalAmountHT: undefined,
	startDate: "",
	deliveryDate: "",
	interventions: {
		architect: {
			ARCHITECT_CONCEPTION: false,
			ARCHITECT_BUILDING_PERMIT_DEPOT: false,
			ARCHITECT_WORK_FOLLOW_UP: false,
		},
		moe: {
			MOE_CONCEPTION: false,
			MOE_BUILDING_PERMIT_DEPOT: false,
			MOE_WORK_FOLLOW_UP: false,
		},
		soil_study: {
			G2AVP: false,
			G2PRO: false,
			G3: false,
			G4: false,
			G5: false,
		},
		technical_control: {
			L: false,
			LP: false,
			LE: false,
			AV: false,
			PS: false,
			S: false,
			SH: false,
			STI: false,
			SEI: false,
			HAND: false,
			BRD: false,
			TH: false,
			PHH: false,
			PHA: false,
			PV: false,
			F: false,
		},
		consulting: {
			STRUCTURAL: false,
			ACOUSTIC: false,
			SANITATION: false,
			INFILTRATION: false,
			GEOTECHNICAL: false,
			THERMAL: false,
			FLUID: false,
			VRD: false,
			HQE: false,
			LIGHTING: false,
			FIRE_SAFETY: false,
		},
	},
	ccmi: false,
	nonUsualMethods: false,
	noOtherOperations: false,
};

const mapConstructionSiteToFormData = (
	constructionSite: ConstructionSiteFullDetails | GFAFullDetails,
): ConstructionSiteDetailsFormData => {
	const details = constructionSite.constructionSiteDetails;
	if (!details) return constructionSiteDetailsDefaultValues;

	const constructionSiteAction = details.constructionSiteAction
		? details.constructionSiteAction
		: null;

	const interventions = details.interventions || [];
	const mappedInterventions = {
		architect: {
			ARCHITECT_CONCEPTION: interventions.includes("ARCHITECT_CONCEPTION"),
			ARCHITECT_BUILDING_PERMIT_DEPOT: interventions.includes(
				"ARCHITECT_BUILDING_PERMIT_DEPOT",
			),
			ARCHITECT_WORK_FOLLOW_UP: interventions.includes(
				"ARCHITECT_WORK_FOLLOW_UP",
			),
		},
		moe: {
			MOE_CONCEPTION: interventions.includes("MOE_CONCEPTION"),
			MOE_BUILDING_PERMIT_DEPOT: interventions.includes(
				"MOE_BUILDING_PERMIT_DEPOT",
			),
			MOE_WORK_FOLLOW_UP: interventions.includes("MOE_WORK_FOLLOW_UP"),
		},
		soil_study: {
			G2AVP: interventions.includes("G2AVP"),
			G2PRO: interventions.includes("G2PRO"),
			G3: interventions.includes("G3"),
			G4: interventions.includes("G4"),
			G5: interventions.includes("G5"),
		},
		technical_control: {
			L: interventions.includes("L"),
			LP: interventions.includes("LP"),
			LE: interventions.includes("LE"),
			AV: interventions.includes("AV"),
			PS: interventions.includes("PS"),
			S: interventions.includes("S"),
			SH: interventions.includes("SH"),
			STI: interventions.includes("STI"),
			SEI: interventions.includes("SEI"),
			HAND: interventions.includes("HAND"),
			BRD: interventions.includes("BRD"),
			TH: interventions.includes("TH"),
			PHH: interventions.includes("PHH"),
			PHA: interventions.includes("PHA"),
			PV: interventions.includes("PV"),
			F: interventions.includes("F"),
		},
		consulting: {
			STRUCTURAL: interventions.includes("STRUCTURAL"),
			ACOUSTIC: interventions.includes("ACOUSTIC"),
			SANITATION: interventions.includes("SANITATION"),
			INFILTRATION: interventions.includes("INFILTRATION"),
			GEOTECHNICAL: interventions.includes("GEOTECHNICAL"),
			THERMAL: interventions.includes("THERMAL"),
			FLUID: interventions.includes("FLUID"),
			VRD: interventions.includes("VRD"),
			HQE: interventions.includes("HQE"),
			LIGHTING: interventions.includes("LIGHTING"),
			FIRE_SAFETY: interventions.includes("FIRE_SAFETY"),
		},
	};

	// Filtrer les types d'actions pour ne garder que les valeurs valides
	const validActionTypes = (constructionSiteAction?.types || []).filter(
		(
			type: string,
		): type is "CONSTRUCTION" | "RENOVATION" | "EXTENSION" | "SYNDIC" =>
			["CONSTRUCTION", "RENOVATION", "EXTENSION", "SYNDIC"].includes(type),
	);
	const validComplementaryActionTypes = (
		constructionSiteAction?.complementaryTypes || []
	).filter(
		(type: string): type is "CONSTRUCTION" | "RENOVATION" | "EXTENSION" =>
			["CONSTRUCTION", "RENOVATION", "EXTENSION"].includes(type),
	);

	const syndicSociety = constructionSite.syndic
		? {
				id: constructionSite.syndic.id,
				siren: constructionSite.syndic.siren,
				denomination: constructionSite.syndic.denomination,
				streetNumber:
					constructionSite.syndic.headOfficeAddress?.streetNumber || "",
				streetName: constructionSite.syndic.headOfficeAddress?.streetName || "",
				postalCode: constructionSite.syndic.headOfficeAddress?.postalCode || "",
				city: constructionSite.syndic.headOfficeAddress?.city || "",
				hasUserAccess: constructionSite.syndic.syndicUserAccessToPlatform
					? "true"
					: "false",
				userAccess: constructionSite.syndic.syndicUserAccessToPlatform
					? {
							lastname:
								constructionSite.syndic.syndicUserAccessToPlatform.lastname,
							firstname:
								constructionSite.syndic.syndicUserAccessToPlatform.firstname,
							email:
								constructionSite.syndic.syndicUserAccessToPlatform.email ?? "",
						}
					: undefined,
				hasBrokerageFees: constructionSite.syndic.brokerageFees
					? "true"
					: "false",
			}
		: {
				...defaultClientSocietyValues,
				isSyndic: true,
			};

	const base = {
		projectName:
			"projectName" in details && details.projectName
				? details.projectName
				: undefined,
		operationType:
			"operationType" in details && details.operationType
				? details.operationType
				: undefined,
		processesType:
			"processesType" in details && details.processesType
				? details.processesType
				: undefined,
		streetNumber: details.address?.streetNumber || "",
		streetName: details.address?.streetName || "",
		postalCode: details.address?.postalCode || "",
		city: details.address?.city || "",
		georisquesAddress: details.georisquesAddress
			? {
					streetNumber: details.georisquesAddress.streetNumber ?? undefined,
					streetName: details.georisquesAddress.streetName ?? undefined,
					postalCode: details.georisquesAddress.postalCode ?? undefined,
					city: details.georisquesAddress.city ?? undefined,
				}
			: undefined,
		rga: details.rga ?? undefined,
		sismic: details.sismic ?? undefined,
		taxMode:
			details.taxMode === "HT" || details.taxMode === "TTC"
				? details.taxMode
				: "TTC",
		totalAmountTTC: details.totalAmountTTC || undefined,
		totalAmountHT: details.totalAmountHT || undefined,
		finalTotalAmount: details.finalTotalAmount || undefined,
		startDate: formatDateForInput(details.startDate?.toString()),
		deliveryDate: formatDateForInput(details.deliveryDate?.toString()),
		cityHallStartDate: formatDateForInput(
			details.cityHallStartDate?.toString(),
		),
		actions: {
			types: validActionTypes,
			buildingType: constructionSiteAction?.buildingType || undefined,
			...(constructionSiteAction?.complementaryTypes &&
			constructionSiteAction?.complementaryBuildingType
				? {
						hasComplementaryActions: true,
						complementaryActions: {
							types: validComplementaryActionTypes,
							buildingType: constructionSiteAction?.complementaryBuildingType,
						},
					}
				: {
						hasComplementaryActions: false,
					}),
			buildingUsage: constructionSiteAction?.buildingUsage || undefined,
			buildingDetails: {
				COMMON_PARTS: {
					buildingsCount:
						constructionSiteAction?.buildingsCount?.toString() || "",
					maxFloors: constructionSiteAction?.maxFloors?.toString() || "",
					maxBasements: constructionSiteAction?.maxBasements?.toString() || "",
					surface: constructionSiteAction?.surface?.toString() || "",
				},
				PRIVATE_WORK: {
					fromFloor: constructionSiteAction?.fromFloor?.toString() || "",
					toFloor: constructionSiteAction?.toFloor?.toString() || "",
					surface: constructionSiteAction?.surface?.toString() || "",
				},
				APPARTMENT: {
					fromFloor: constructionSiteAction?.fromFloor?.toString() || "",
					toFloor: constructionSiteAction?.toFloor?.toString() || "",
					surface: constructionSiteAction?.surface?.toString() || "",
					extensionSurface:
						constructionSiteAction?.extensionSurface?.toString() || "",
				},
				INDIVIDUAL_HOUSE: {
					floorsCount: constructionSiteAction?.floorsCount?.toString() || "",
					basementsCount:
						constructionSiteAction?.basementsCount?.toString() || "",
					surface: constructionSiteAction?.surface?.toString() || "",
					habitationsCount:
						constructionSiteAction?.habitationsCount?.toString() || "",
					extensionSurface:
						constructionSiteAction?.extensionSurface?.toString() || "",
				},
				LIVING_BUILDING: {
					buildingsCount:
						constructionSiteAction?.buildingsCount?.toString() || "",
					maxFloors: constructionSiteAction?.maxFloors?.toString() || "",
					maxBasements: constructionSiteAction?.maxBasements?.toString() || "",
					surface: constructionSiteAction?.surface?.toString() || "",
					habitationsCount:
						constructionSiteAction?.habitationsCount?.toString() || "",
					extensionSurface:
						constructionSiteAction?.extensionSurface?.toString() || "",
				},
				LOT_HOUSES: {
					buildingsCount:
						constructionSiteAction?.buildingsCount?.toString() || "",
					maxFloors: constructionSiteAction?.maxFloors?.toString() || "",
					maxBasements: constructionSiteAction?.maxBasements?.toString() || "",
					surface: constructionSiteAction?.surface?.toString() || "",
					habitationsCount:
						constructionSiteAction?.habitationsCount?.toString() || "",
					extensionSurface:
						constructionSiteAction?.extensionSurface?.toString() || "",
				},
				OFFICE_BUILDING: {
					buildingsCount:
						constructionSiteAction?.buildingsCount?.toString() || "",
					maxFloors: constructionSiteAction?.maxFloors?.toString() || "",
					maxBasements: constructionSiteAction?.maxBasements?.toString() || "",
					surface: constructionSiteAction?.surface?.toString() || "",
					extensionSurface:
						constructionSiteAction?.extensionSurface?.toString() || "",
				},
				COMMERCIAL_BUILDING: {
					buildingsCount:
						constructionSiteAction?.buildingsCount?.toString() || "",
					maxFloors: constructionSiteAction?.maxFloors?.toString() || "",
					maxBasements: constructionSiteAction?.maxBasements?.toString() || "",
					surface: constructionSiteAction?.surface?.toString() || "",
					extensionSurface:
						constructionSiteAction?.extensionSurface?.toString() || "",
				},
				MIXT_BUILDING: {
					buildingsCount:
						constructionSiteAction?.buildingsCount?.toString() || "",
					maxFloors: constructionSiteAction?.maxFloors?.toString() || "",
					maxBasements: constructionSiteAction?.maxBasements?.toString() || "",
					surface: constructionSiteAction?.surface?.toString() || "",
					habitationsCount:
						constructionSiteAction?.habitationsCount?.toString() || "",
					habitationsSize:
						constructionSiteAction?.habitationsSize?.toString() || "",
					officesSize: constructionSiteAction?.officesSize?.toString() || "",
					commercialBuildingsSize:
						constructionSiteAction?.commercialBuildingsSize?.toString() || "",
					extensionSurface:
						constructionSiteAction?.extensionSurface?.toString() || "",
				},
			},
			underpinningExistingFoundations:
				constructionSiteAction?.underpinningExistingFoundations || false,
			interventionOnLoadBearingStructure:
				constructionSiteAction?.interventionOnLoadBearingStructure || false,
			socialLandlordRental:
				constructionSiteAction?.socialLandlordRental || false,
		},
		interventions: mappedInterventions,
		ccmi: details.ccmi || false,
		nonUsualMethods: details.nonUsualMethods || false,
		noOtherOperations: details.noOtherOperations || false,
	};

	if (
		constructionSite.syndic &&
		constructionSite.syndic.id !==
			constructionSite.constructionSiteClientDetails?.clientSociety?.id
	) {
		return {
			...base,
			throughSyndic: true,
			syndicSociety,
		};
	}

	return {
		...base,
		throughSyndic: false,
	};
};

export function useConstructionSiteDetailsForm(
	constructionSite?: ConstructionSiteFullDetails | GFAFullDetails | null,
) {
	const defaultValues = constructionSite
		? mapConstructionSiteToFormData(constructionSite)
		: constructionSiteDetailsDefaultValues;
	const schema = constructionSiteDetailsSchema;
	type FormData = z.infer<typeof schema>;
	const form = useForm<FormData>({
		mode: "all",
		defaultValues,
		resolver: zodResolver(schema) as Resolver<FormData>,
	});

	const { reset } = form;
	useEffect(() => {
		if (constructionSite) {
			reset(mapConstructionSiteToFormData(constructionSite));
		}
	}, [reset, constructionSite]);
	return { form, defaultValues };
}

export const bypassedFormValues: ConstructionSiteDetailsFormData = {
	streetNumber: "25",
	streetName: "Rue Lecourbe",
	postalCode: "75015",
	city: "Paris",
	throughSyndic: false,
	actions: {
		types: ["RENOVATION"],
		buildingDetails: {
			APPARTMENT: {
				fromFloor: "",
				toFloor: "",
				surface: "",
				extensionSurface: "",
			},
			INDIVIDUAL_HOUSE: {
				floorsCount: "4",
				basementsCount: "2",
				surface: "1",
				habitationsCount: "1",
				extensionSurface: "",
			},
			LIVING_BUILDING: {
				buildingsCount: "",
				maxFloors: "",
				maxBasements: "",
				surface: "",
				habitationsCount: "",
				extensionSurface: "",
			},
			LOT_HOUSES: {
				buildingsCount: "",
				maxFloors: "",
				maxBasements: "",
				surface: "",
				habitationsCount: "",
				extensionSurface: "",
			},
			OFFICE_BUILDING: {
				buildingsCount: "",
				maxFloors: "",
				maxBasements: "",
				surface: "",
				extensionSurface: "",
			},
			COMMERCIAL_BUILDING: {
				buildingsCount: "",
				maxFloors: "",
				maxBasements: "",
				surface: "",
				extensionSurface: "",
			},
			MIXT_BUILDING: {
				buildingsCount: "",
				maxFloors: "",
				maxBasements: "",
				surface: "",
				habitationsCount: "",
				habitationsSize: "",
				officesSize: "",
				commercialBuildingsSize: "",
				extensionSurface: "",
			},
			PRIVATE_WORK: {
				fromFloor: "",
				toFloor: "",
				surface: "",
			},
			COMMON_PARTS: {
				buildingsCount: "",
				maxFloors: "",
				maxBasements: "",
				surface: "",
			},
		},
		underpinningExistingFoundations: true,
		interventionOnLoadBearingStructure: true,
		socialLandlordRental: true,
		hasComplementaryActions: false,
		buildingType: "INDIVIDUAL_HOUSE",
	},
	taxMode: "TTC",
	totalAmountTTC: 500000,
	startDate: "2025-09-01",
	deliveryDate: "2025-12-31",
	interventions: {
		architect: {
			ARCHITECT_CONCEPTION: false,
			ARCHITECT_BUILDING_PERMIT_DEPOT: false,
			ARCHITECT_WORK_FOLLOW_UP: false,
		},
		moe: {
			MOE_CONCEPTION: true,
			MOE_BUILDING_PERMIT_DEPOT: true,
			MOE_WORK_FOLLOW_UP: true,
		},
		soil_study: {
			G2AVP: true,
			G2PRO: true,
			G3: false,
			G4: false,
			G5: false,
		},
		technical_control: {
			L: true,
			LP: true,
			LE: true,
			AV: false,
			PS: true,
			S: false,
			SH: false,
			STI: false,
			SEI: false,
			HAND: false,
			BRD: false,
			TH: false,
			PHH: false,
			PHA: false,
			PV: false,
			F: false,
		},
		consulting: {
			STRUCTURAL: true,
			ACOUSTIC: false,
			SANITATION: false,
			INFILTRATION: false,
			GEOTECHNICAL: false,
			THERMAL: false,
			FLUID: false,
			VRD: false,
			HQE: false,
			LIGHTING: false,
			FIRE_SAFETY: false,
		},
	},
	ccmi: false,
	nonUsualMethods: true,
	noOtherOperations: false,
	sismic: 0,
};
